/**
 * useAuth.js — shared auth store (no provider needed; module-level external store
 * via useSyncExternalStore so every modal sees the same user/token).
 *
 *   loginTelegram(data)  — POST /auth/telegram  (Telegram Login Widget payload)
 *   loginGoogle(cred)    — POST /auth/google    (Google ID token / dev email)
 *   refresh()            — GET  /me
 *   updateProfile(patch) — PATCH /me
 *   logout()             — clears JWT
 *
 * The JWT is persisted in localStorage (`qr_token`) by the api client.
 */
import { useSyncExternalStore } from 'react';
import { api, setToken, getToken } from '../api/client';
import { _setUserId } from '../utils/userStorage';
import { getFingerprint } from '../utils/fingerprint';

const listeners = new Set();
let state = { user: null, token: getToken(), loading: !!getToken(), error: null };

function emit() { listeners.forEach((l) => l()); }
function set(patch) {
  state = { ...state, ...patch };
  // Keep userStorage in sync so all modules read the right namespace
  _setUserId(state.user?.id ?? null);
  emit();
}
function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }
const snapshot = () => state;

async function handleAuthResponse(res) {
  setToken(res.token);
  set({ user: res.user, token: res.token, loading: false, error: null });
  return res.user;
}

// idToken: JWT string from new Telegram OIDC flow (data.id_token from the widget callback)
// In dev mode without a configured widget, pass a plain object { id, username, ... } instead.
export async function loginTelegram(idToken, nickname) {
  set({ loading: true, error: null });
  try {
    const fingerprint = getFingerprint();
    const body = typeof idToken === 'string'
      ? { id_token: idToken, nickname: nickname || undefined, fingerprint }
      : { ...idToken, fingerprint }; // dev fallback object
    return await handleAuthResponse(await api('/auth/telegram', { method: 'POST', body, auth: false }));
  }
  catch (e) { set({ loading: false, error: e.message }); throw e; }
}

// Called when user lands on site with ?tg_token= in the URL (bot login flow).
export async function loginWithBotToken(tgToken) {
  set({ loading: true, error: null });
  try {
    return await handleAuthResponse(
      await api('/auth/telegram/bot-login', { method: 'POST', body: { tg_token: tgToken, fingerprint: getFingerprint() }, auth: false })
    );
  } catch (e) {
    set({ loading: false, error: e.message });
    throw e;
  }
}

export async function loginGoogle(credential, nickname) {
  set({ loading: true, error: null });
  try { return await handleAuthResponse(await api('/auth/google', { method: 'POST', body: { credential, nickname, fingerprint: getFingerprint() }, auth: false })); }
  catch (e) { set({ loading: false, error: e.message }); throw e; }
}

export async function refresh() {
  if (!getToken()) { set({ loading: false }); return null; }
  set({ loading: true });
  try { const { user } = await api('/me'); set({ user, loading: false, error: null }); return user; }
  catch (e) {
    if (e.status === 401) { setToken(null); set({ user: null, token: null, loading: false }); }
    else set({ loading: false, error: e.message });
    return null;
  }
}

export async function updateProfile(patch) {
  const { user } = await api('/me', { method: 'PATCH', body: patch });
  set({ user });
  return user;
}

// Called by AuthModal polling when /auth/telegram/status returns ok.
export function setAuthResult({ token, user }) {
  setToken(token);
  set({ user, token, loading: false, error: null });
}

// Merge a fresher user object (e.g. returned by billing confirm) into the store.
export function setUser(user) { set({ user }); }

export function logout() {
  setToken(null);
  set({ user: null, token: null, loading: false });
  // Clear the session kline cache (plain object on window, set by UplotChart)
  try {
    if (window.__uplotKlineCache) {
      Object.keys(window.__uplotKlineCache).forEach(k => delete window.__uplotKlineCache[k]);
    }
  } catch {}
}

// Refresh the profile once on first load if a token exists.
// В lab-режиме (?lab=1, LAB.bat, только dev-сборка) при отсутствии токена
// логинимся сами через dev-вход бэкенда (ALLOW_DEV_AUTH) — владелец попадает
// сразу в терминал для разметки, без формы входа.
let booted = false;
function boot() {
  if (booted) return;
  booted = true;
  if (getToken()) { refresh(); return; }
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('lab')) {
    loginGoogle('lab@local.dev', 'Labeler').catch((e) =>
      console.warn('[lab] dev auto-login failed (is the backend running with ALLOW_DEV_AUTH?):', e.message));
  }
}

export function useAuth() {
  boot();
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return {
    user: s.user,
    token: s.token,
    loading: s.loading,
    error: s.error,
    isAuthenticated: !!s.user,
    loginTelegram,
    loginWithBotToken,
    loginGoogle,
    setAuthResult,
    refresh,
    updateProfile,
    setUser,
    logout,
  };
}
